import React, { FC } from "react";
import ReactGA from "react-ga4";
import {
    FollowEntity,
    Hashtag,
    Maybe,
    PlatformPermissionFromFeatureTypes,
    useFollowMutation,
    useGetFeatureToPlatformPermissionQuery,
    useUnfollowMutation
} from "api";
import { updateCacheOnFollowHashTag, updateCacheOnUnFollowHashTag } from "config/apollo";
import { useUpgradePlanContext } from "context/UpgradePlan/UpgradePlanContext";

import {
    CLICK_UPGRADE,
    FOLLOW_TOPIC,
    UNFOLLOW_TOPIC,
    getEventScreen
} from "utills/events";
import { Button, ButtonProps } from "@mui/material";
import { usePermissionFeatureMapping } from "hooks";

interface HashtagFollowUnfollowButtonProps extends Exclude<ButtonProps, "onClick"> {
    hashtag: Maybe<Hashtag> | undefined;
    hideUnfollowButton?: boolean;
}

export const HashtagFollowUnfollowButton: FC<HashtagFollowUnfollowButtonProps> = ({ hashtag, hideUnfollowButton, ...rest }) => {
    if (!hashtag) return null;

    const { platformFeatureMap } = usePermissionFeatureMapping();
    const [follow] = useFollowMutation();
    const [unfollow] = useUnfollowMutation();
    const { handleModalOpen } = useUpgradePlanContext();
    const { data } = useGetFeatureToPlatformPermissionQuery();

    const handleFollow = React.useCallback((event: React.SyntheticEvent) => {
        event.preventDefault();
        if (!data?.getFeatureToPlatformPermission?.includes(PlatformPermissionFromFeatureTypes.CanFollow)) {
            ReactGA.event(CLICK_UPGRADE, {
                event_screen: getEventScreen(),
                features: platformFeatureMap[PlatformPermissionFromFeatureTypes.CanFollow]
            });
            handleModalOpen();
            return;
        }
        ReactGA.event(FOLLOW_TOPIC, {
            event_screen: getEventScreen()
        });
        follow({
            variables: {
                data: {
                    id: `${hashtag?.hashtagName}`,
                    entity: FollowEntity.Hashtag,
                }
            },
            update: (cache) => {
                updateCacheOnFollowHashTag(cache, hashtag);
            }
        })
    }, [hashtag?.hashtagName, data?.getFeatureToPlatformPermission]);

    const handleUnfollow = React.useCallback((event: React.SyntheticEvent) => {
        event.preventDefault();
        if (!data?.getFeatureToPlatformPermission?.includes(PlatformPermissionFromFeatureTypes.CanFollow)) {
            ReactGA.event(CLICK_UPGRADE, {
                event_screen: getEventScreen(),
                features: platformFeatureMap[PlatformPermissionFromFeatureTypes.CanFollow]
            });
            handleModalOpen();
            return;
        }
        ReactGA.event(UNFOLLOW_TOPIC, {
            event_screen: getEventScreen()
        });
        unfollow({
            variables: {
                data: {
                    id: `${hashtag?.hashtagName}`,
                    entity: FollowEntity.Hashtag,
                }
            },
            update: (cache) => {
                updateCacheOnUnFollowHashTag(cache, hashtag);
            }
        });

    }, [hashtag?.hashtagName, data?.getFeatureToPlatformPermission]);

    if (!hashtag?.isfollow) return (
        <Button
            variant="contained"
            disableElevation
            onClick={handleFollow}
            {...rest}
        >Follow</Button>
    )
    if (hashtag?.isfollow && !hideUnfollowButton) return (
        <Button
            variant="contained"
            color="secondary"
            disableElevation
            onClick={handleUnfollow}
            {...rest}
        >Unfollow</Button>
    )
    return null;
}

